// =============================================================================
// app/src/lib/aVerDoc.ts — o documento impresso do "a ver".
//
// Abre numa janela própria com HTML autocontido (sem o CSS do app), duas vias
// na mesma folha — uma fica com o cliente, outra na loja, ambas assinadas.
// Os termos impressos são a regra do negócio: não negociável, não transferível,
// sem conversão em dinheiro.
//
// Duas decisões aqui são de segurança, não de estética:
//
//  * TODO valor vindo do banco passa por `escaparHtml()`. A janela nasce de
//    `window.open('')`, e about:blank HERDA a origem do app — um
//    `<img src=x onerror=…>` gravado em cliente_nome/cliente_doc/motivo por
//    quem emite o vale executaria dentro de sistema.serginhoautopeças.com.br,
//    com acesso ao localStorage onde vive o token de sessão de QUEM IMPRIME.
//    Como quem reimprime costuma ser o dono, isso era escalada de gerente para
//    dono — exatamente o que a hierarquia de papéis existe para impedir.
//  * O documento não tem script nenhum e declara CSP `script-src 'none'`:
//    se um dia faltar um escape, ainda assim nada roda. Quem chama `print()`
//    é o opener, depois do `document.close()`.
//
// E uma decisão de negócio: a REIMPRESSÃO mostra o saldo de hoje, não o valor
// de emissão. Papel timbrado dizendo R$ 300 sobre um vale de R$ 80 (ou sobre um
// vale cancelado) é briga de balcão garantida.
// =============================================================================
import type { StoreCredit } from '@core/types';
import { brl, dataBR, dataHoraBR, hojeISO } from './formato';

const ESCAPE_HTML: Record<string, string> = {
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
};

/**
 * Escapa dado do banco antes de entrar no HTML do documento impresso.
 * Nenhuma interpolação de campo de `store_credits` pode escapar desta função.
 */
export const escaparHtml = (v: unknown): string =>
  String(v ?? '').replace(/[&<>"']/g, (c) => ESCAPE_HTML[c]!);

/**
 * A data que o PAPEL mostra: `emitido_em` (migração 021), não `created_at`.
 *
 * Num vale transcrito — o passivo antigo escrito à mão, o que o balcão emitiu à
 * caneta na falta de luz — a data que o cliente reconhece é a do papel que ele
 * tem na mão; `created_at` é quando alguém digitou. O fallback não é
 * paranoia: a coluna nasceu na 021 e o tipo a declara obrigatória, mas uma
 * linha lida de um banco ainda sem a migração chegaria sem ela, e um documento
 * dizendo "Invalid Date" no lugar da data é pior do que um dia de defasagem.
 */
export function dataDeEmissao(credito: StoreCredit): string {
  return (credito as { emitido_em?: string }).emitido_em ?? credito.created_at;
}

const MESES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

/**
 * "7 de agosto de 2026" — a data por extenso que acompanha a assinatura.
 *
 * Por extenso, e não 07/08/2026, porque num documento assinado à mão a data em
 * números é o campo mais fácil de adulterar: um "1" vira "7" com uma canetada.
 * Ela repete a data que já está na tabela acima de propósito — a tabela é
 * conferência, esta linha é parte do que está sendo assinado.
 *
 * `new Date` sobre um timestamp com hora é seguro (ao contrário de date-only,
 * que viraria meia-noite UTC e cairia no dia anterior em Rio Grande) — e
 * `emitido_em` é sempre timestamptz.
 */
export function dataPorExtenso(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} de ${MESES[d.getMonth()]} de ${d.getFullYear()}`;
}

// O SÍMBOLO SAI EM PRETO NO PAPEL, e isso é decisão de operação (20/08/2026).
// Na tela e no site ele é o vermelho da marca (#D90429, regra 10). Aqui não:
// UM pixel colorido faz a HP jato de tinta trocar para o modo em cores, e a
// página que sai em segundos passa a levar quase um minuto — com o cliente
// esperando no balcão. O agente ainda manda `print-color-mode: monochrome` por
// IPP; este preto é o cinto além do suspensório (documento que a loja imprime
// fora do agente também sai rápido).
export const S_LOGO = `<svg viewBox="0 0 738 502" width="34" style="vertical-align:middle" xmlns="http://www.w3.org/2000/svg"><path fill="#111111" d="M265.16 197.096L716.099 197.133C710.828 236.013 703.583 277.477 697.547 316.427L691.319 357.303C688.727 374.581 687.153 388.315 681.173 404.926C673.717 426.092 661.62 445.323 645.765 461.208C626.687 480.431 602.358 493.601 575.825 499.063C558.852 502.518 534.196 501.482 516.359 501.482L0 501.506C6.45146 483.313 20.0753 438.942 29.209 424.596C33.8706 417.227 39.9357 410.846 47.0599 405.817C68.5016 390.473 90.0878 393.236 114.993 393.246L467.663 393.298C480.383 393.309 491.548 393.616 505.058 391.956C536.745 388.419 573.091 341.625 550.341 312.787C542.231 302.501 520.482 305.103 508.558 305.102L240.273 305.049L265.16 197.096Z"/><path fill="#111111" d="M195.821 0.543453C213.607 -0.496412 240.032 0.274996 258.347 0.279316L737.602 0.30894L726.382 34.5152C715.525 67.7255 704.255 100.888 663.231 107.702C652.029 109.563 638.877 108.711 627.422 108.697L297.094 108.625C283.826 108.599 258.16 107.708 246.463 110.265C234.27 113.024 222.913 118.655 213.336 126.689C202.159 136.104 193.793 148.416 189.157 162.274C184.055 177.316 183.118 204.508 181.773 221.171L174.91 305.148L27.0847 305.094C27.9432 296.569 30.2866 284.133 31.7117 275.497L41.2102 218.07L50.7884 158.008C53.5367 140.681 55.7487 122.604 61.4157 106.006C68.0344 86.9096 78.1359 69.2054 91.208 53.7901C117.493 22.6886 155.205 3.49334 195.821 0.543453Z"/></svg>`;

export interface OpcoesAVerDoc {
  /** true na emissão (documento novo). Ausente/false = 2ª via, que imprime o saldo. */
  emissao?: boolean;
  /**
   * Quem assina pela loja. Uma linha em branco rotulada "Loja" não diz QUEM
   * assumiu o compromisso — e o vale é uma dívida da loja com o cliente. Com o
   * nome impresso embaixo da linha, a assinatura passa a ter dono.
   * Vazio (vale antigo, perfil não legível) cai no rótulo genérico.
   */
  emitidoPor?: string | null;
  /** Momento da impressão — injetável para o teste; padrão `new Date()`. */
  agora?: Date;
}

/**
 * O que a 2ª via precisa dizer em voz alta: o vale ainda vale?
 *
 * Exportada porque o PDF (aVerPdf.ts) monta o MESMO documento por outro
 * caminho. Duplicar estas três condições seria deixá-las divergir — e o dia em
 * que o HTML disser "CANCELADO" e o PDF não é o dia em que a loja honra um vale
 * morto.
 */
export function tarjaDe(credito: StoreCredit, agora: Date): string | null {
  if (credito.cancelado_em) {
    return `CANCELADO EM ${dataBR(credito.cancelado_em)} — SEM VALOR`;
  }
  if (Number(credito.saldo) <= 0) return 'SALDO ESGOTADO — SEM VALOR';
  if (credito.valido_ate && credito.valido_ate < hojeISO(agora)) {
    return `VENCIDO EM ${dataBR(`${credito.valido_ate}T12:00:00`)} — SEM VALOR`;
  }
  return null;
}

function via(credito: StoreCredit, rotuloVia: string, opcoes: Required<OpcoesAVerDoc>): string {
  const { emissao, agora } = opcoes;
  const tarja = emissao ? null : tarjaDe(credito, agora);
  const semValor = tarja !== null;
  const original = brl(Number(credito.valor_original));

  // Na emissão o número é o valor contratado; na 2ª via é o saldo apurado
  // agora, com o original como referência de conferência.
  const bloco = emissao
    ? `<span class="rot">Valor em crédito</span>
       <span class="num">${original}</span>`
    : `<span class="rot">Saldo disponível
         <small>em ${dataHoraBR(agora.toISOString())} · valor original ${original}</small>
       </span>
       <span class="num">${brl(Number(credito.saldo))}</span>`;

  return `
  <div class="via">
    <header>
      <div class="marca">${S_LOGO}<span class="nome">SERGINHO</span><span class="sub">AUTO PEÇAS</span></div>
      <div class="doc">
        <span class="titulo">VALE "A VER"${emissao ? '' : ' · REIMPRESSÃO'}</span>
        <span class="codigo">${escaparHtml(credito.codigo)}</span>
      </div>
    </header>

    ${tarja ? `<p class="tarja">${escaparHtml(tarja)}</p>` : ''}

    <div class="valor${semValor ? ' anulado' : ''}">${bloco}</div>

    <table>
      <tr><td class="rot">Cliente</td><td>${escaparHtml(credito.cliente_nome)}</td></tr>
      ${credito.cliente_doc ? `<tr><td class="rot">CPF/CNPJ</td><td>${escaparHtml(credito.cliente_doc)}</td></tr>` : ''}
      ${credito.motivo ? `<tr><td class="rot">Referente a</td><td>${escaparHtml(credito.motivo)}</td></tr>` : ''}
      <tr><td class="rot">Emitido em</td><td>${escaparHtml(dataBR(dataDeEmissao(credito)))}</td></tr>
      <tr><td class="rot">Válido até</td><td>${credito.valido_ate ? escaparHtml(dataBR(`${credito.valido_ate}T12:00:00`)) : 'sem vencimento'}</td></tr>
    </table>

    ${semValor
      ? `<p class="termos">
      Este documento é apenas o histórico do vale acima: <strong>não dá direito a
      crédito e não pode ser apresentado como vale válido</strong>. O código
      continua auditável no sistema da loja.
    </p>`
      : `<p class="termos">
      Este vale dá direito a crédito no valor acima para compra de peças nesta loja.
      <strong>Não é negociável, não é transferível e não pode ser convertido em dinheiro.</strong>
      Válido somente para o cliente identificado, mediante apresentação deste documento.
      ${emissao
        ? 'Pode ser usado em mais de uma compra até esgotar o saldo.'
        : 'Vale o SALDO impresso acima, apurado na data desta reimpressão — compras posteriores o reduzem.'}
    </p>`}

    ${semValor ? '' : `
    <p class="local-data">Rio Grande/RS, ${escaparHtml(dataPorExtenso(dataDeEmissao(credito)))}.</p>

    <div class="assinaturas">
      <div>
        <span></span>
        <strong>Serginho Autopeças</strong>
        ${opcoes.emitidoPor ? `<em>${escaparHtml(opcoes.emitidoPor)}</em>` : '<em>Responsável pela emissão</em>'}
      </div>
      <div>
        <span></span>
        <strong>${escaparHtml(credito.cliente_nome)}</strong>
        <em>${credito.cliente_doc ? escaparHtml(credito.cliente_doc) : 'Cliente'}</em>
      </div>
    </div>

    <p class="como-usar">
      <strong>Para usar:</strong> apresente esta via e informe o código
      <strong class="cod">${escaparHtml(credito.codigo)}</strong> no balcão.
    </p>`}

    <p class="rodape">${escaparHtml(rotuloVia)} · ${escaparHtml(credito.codigo)}${
      emissao ? '' : ` · reimpresso em ${escaparHtml(dataHoraBR(agora.toISOString()))}`
    }</p>
  </div>`;
}

/** Monta o HTML completo. Separado de `imprimirAVer` para poder ser testado. */
export function montarDocumentoAVer(credito: StoreCredit, opcoes: OpcoesAVerDoc = {}): string {
  const cfg: Required<OpcoesAVerDoc> = {
    emissao: opcoes.emissao ?? false,
    agora: opcoes.agora ?? new Date(),
    emitidoPor: opcoes.emitidoPor ?? null,
  };
  const rotulos = cfg.emissao
    ? ['1ª via — cliente', '2ª via — loja']
    : ['Reimpressão — via do cliente', 'Reimpressão — via da loja'];

  // Sem <script> aqui dentro, de propósito: com script-src 'none' um escape
  // que falhe no futuro vira texto na folha, não código na origem do app.
  return `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy"
      content="default-src 'none'; img-src data:; style-src 'unsafe-inline'; script-src 'none'; form-action 'none'">
<title>${escaparHtml(credito.codigo)} — Vale a ver</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  /* AS MEDIDAS DAQUI PARA BAIXO SÃO ORÇAMENTO DE PÁGINA, NÃO GOSTO.
     São DUAS vias numa folha A4 (1123 px CSS de altura), e o que passar disso
     é cortado pelo agente de impressão, que fotografa exatamente um A4. Ao
     acrescentar qualquer linha aqui, meça de novo: o documento inteiro precisa
     caber em 1123 px. Foi assim que o bloco de assinaturas entrou. */
  body { font-family: Arial, Helvetica, sans-serif; color: #111; padding: 7mm; }
  .via { border: 2px solid #111; padding: 5mm; margin-bottom: 4mm; page-break-inside: avoid; }
  header { display: flex; justify-content: space-between; align-items: center;
           border-bottom: 2px solid #111; padding-bottom: 2.5mm; margin-bottom: 3mm; }
  .marca .nome { font-weight: 900; font-style: italic; font-size: 18px; margin-left: 6px; letter-spacing: .5px; }
  .marca .sub { font-size: 9px; letter-spacing: 2px; margin-left: 6px; color: #555; }
  .doc { text-align: right; }
  .doc .titulo { display: block; font-size: 11px; letter-spacing: 2px; }
  .doc .codigo { display: block; font-weight: 900; font-size: 18px; font-family: monospace; }
  .tarja { border: 3px solid #111; background: #111; color: #fff; text-align: center;
           font-weight: 900; font-size: 15px; letter-spacing: 3px; padding: 3mm; margin-bottom: 5mm; }
  .valor { display: flex; justify-content: space-between; align-items: baseline;
           background: #f2f2f2; padding: 3mm; margin-bottom: 3mm; }
  .valor.anulado .num { text-decoration: line-through; }
  .valor .rot { font-size: 11px; letter-spacing: 1px; text-transform: uppercase; }
  .valor .rot small { display: block; letter-spacing: 0; text-transform: none;
                      font-size: 10px; color: #555; margin-top: 1mm; }
  .valor .num { font-size: 26px; font-weight: 900; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 3mm; }
  td { padding: 1.2mm 0; font-size: 12.5px; border-bottom: 1px solid #ddd; }
  td.rot { width: 32mm; color: #555; font-size: 11px; text-transform: uppercase; letter-spacing: .5px; }
  .termos { font-size: 10px; line-height: 1.4; color: #333; margin-bottom: 3mm; }
  /* Espaço para a caneta. Não é margem estética: é onde a assinatura vai ser
     escrita à mão. Com 9mm a assinatura invadia a linha da data; 14mm é o vão
     em que uma firma adulta cabe inteira. Ainda sobram ~22mm de folga na
     folha depois disso — medido, não estimado. */
  .local-data { font-size: 10.5px; text-align: right; margin-bottom: 14mm; }
  .assinaturas { display: flex; gap: 12mm; margin-bottom: 2.5mm; }
  .assinaturas div { flex: 1; text-align: center; font-size: 11px; }
  .assinaturas span { display: block; border-top: 1px solid #111; margin-bottom: 1.5mm; padding-top: 1.5mm; }
  .assinaturas strong { display: block; font-size: 11px; }
  .assinaturas em { display: block; font-style: normal; font-size: 9.5px; color: #555; }
  .como-usar { border: 1px solid #111; padding: 2mm 3mm; font-size: 10.5px;
               margin-bottom: 2mm; }
  .como-usar .cod { font-family: monospace; font-size: 12.5px; }
  .rodape { font-size: 9px; color: #777; text-align: right; }
  @media print { body { padding: 0; } }
</style></head>
<body>
  ${via(credito, rotulos[0]!, cfg)}
  ${via(credito, rotulos[1]!, cfg)}
</body></html>`;
}

export function imprimirAVer(credito: StoreCredit, opcoes: OpcoesAVerDoc = {}): void {
  const janela = window.open('', '_blank', 'width=800,height=900');
  if (!janela) return;
  janela.document.write(montarDocumentoAVer(credito, opcoes));
  janela.document.close();
  // O documento já está completo depois do close(); quem imprime é o opener,
  // porque o próprio documento não tem (nem pode ter) script.
  janela.focus();
  janela.print();
}
